import React, { useState } from "react";

export default function IntonationFeedbackUsers({ assessment }) {
  const { 
    correlation, 
    slope_diff,
    intensity_correlation,
    intensity_slope_diff 
  } = assessment;

  // State for showing playback speed suggestions
  const [showSpeedTips, setShowSpeedTips] = useState(false);
  const [showMicTips, setShowMicTips] = useState(false);
  
  // Check if intensity data is available
  const hasIntensity = intensity_correlation !== undefined;

  // ===============================
  // Check for microphone/recording issues
  // ===============================
  const hasRecordingIssue = correlation === 0 && slope_diff > 0.5;

  // ===============================
  // If recording issue, show simple microphone warning
  // ===============================
  if (hasRecordingIssue) {
    return (
      <div className="flex flex-col space-y-4 w-full max-w-full">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-800">Intonation Direction Analysis</h2>
          <p className="text-sm text-gray-600 mt-1">
            How well your pitch and volume follow the narrator's patterns
          </p>
        </div>

        {/* Simple Microphone Warning */}
        <div className="bg-orange-100 border border-orange-400 text-orange-800 px-4 py-3 rounded-lg">
          <div className="flex items-start">
            <span className="text-2xl mr-3">🎤</span>
            <div className="flex-1">
              <strong className="block text-lg mb-2">Microphone Issue Detected</strong>
              <p className="text-sm mb-3">
                We couldn't analyze your recording. Your microphone may be off or no sound was detected.
              </p>
              
              <button 
                onClick={() => setShowMicTips(!showMicTips)}
                className="w-full text-left px-3 py-2 bg-orange-200 hover:bg-orange-300 text-orange-800 rounded border border-orange-300 transition-colors flex items-center justify-between"
              >
                <span>{showMicTips ? "Hide Fixes" : "Show Quick Fixes"}</span>
                <span>{showMicTips ? "▲" : "▼"}</span>
              </button>
              
              {showMicTips && (
                <div className="mt-3 p-3 bg-white rounded border border-orange-200">
                  <ul className="text-sm space-y-2">
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2">•</span>
                      <span>Check microphone connection and mute status</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2">•</span>
                      <span>Grant microphone permissions in browser</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2">•</span>
                      <span>Speak clearly and loudly</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2">•</span>
                      <span>Record in a quiet environment</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2">•</span>
                      <span>Try recording again after checking</span>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===============================
  // Get pitch direction feedback
  // ===============================
  const getPitchDirectionFeedback = () => {
    if (correlation < 0) {
      return {
        rating: "Opposite Direction",
        color: "bg-red-100 text-red-800 border-red-300",
        emoji: "🔄",
        message: "Your pitch moves opposite to the narrator.",
        suggestion: "Mimic their upward/downward movements.",
        needsSlowerSpeed: true,
        recommendedSpeed: "0.25x"
      };
    }
    
    if (slope_diff < 0.08) {
      return {
        rating: "Perfect Match",
        color: "bg-green-100 text-green-800 border-green-300",
        emoji: "🎯",
        message: "Excellent pitch direction match.",
        suggestion: "Great job! Keep practicing.",
        needsSlowerSpeed: false
      };
    }
    
    if (slope_diff < 0.18) {
      return {
        rating: "Good Match",
        color: "bg-yellow-100 text-yellow-800 border-yellow-300",
        emoji: "👍",
        message: "Good direction, could be more precise.",
        suggestion: "Match exact degree of changes.",
        needsSlowerSpeed: true,
        recommendedSpeed: "0.5x"
      };
    }
    
    return {
      rating: "Needs Work",
      color: "bg-orange-100 text-orange-800 border-orange-300",
      emoji: "📊",
      message: "Pitch trend doesn't match closely.",
      suggestion: "Match overall rising/falling trend.",
      needsSlowerSpeed: true,
      recommendedSpeed: "0.25x"
    };
  };

  // ===============================
  // Get volume direction feedback
  // ===============================
  const getVolumeDirectionFeedback = () => {
    if (!hasIntensity) return null;
    
    if (intensity_correlation < 0) {
      return {
        rating: "Opposite Pattern",
        color: "bg-red-100 text-red-800 border-red-300",
        emoji: "🔁",
        message: "Volume changes opposite to narrator.",
        suggestion: "Get louder/quieter when they do.",
        needsSlowerSpeed: true,
        recommendedSpeed: "0.25x"
      };
    }
    
    if (intensity_slope_diff < 0.06) {
      return {
        rating: "Perfect Volume",
        color: "bg-green-100 text-green-800 border-green-300",
        emoji: "🔊",
        message: "Excellent volume pattern match.",
        suggestion: "Perfect emphasis matching.",
        needsSlowerSpeed: false
      };
    }
    
    if (intensity_slope_diff < 0.15) {
      return {
        rating: "Good Volume",
        color: "bg-yellow-100 text-yellow-800 border-yellow-300",
        emoji: "🔉",
        message: "Good volume pattern.",
        suggestion: "Match timing and intensity.",
        needsSlowerSpeed: true,
        recommendedSpeed: "0.5x"
      };
    }
    
    return {
      rating: "Needs Work",
      color: "bg-orange-100 text-orange-800 border-orange-300",
      emoji: "📈",
      message: "Volume trend doesn't match.",
      suggestion: "Emphasize where narrator does.",
      needsSlowerSpeed: true,
      recommendedSpeed: "0.25x"
    };
  };

  // ===============================
  // Get overall combined feedback
  // ===============================
  const getOverallFeedback = () => {
    const pitchFeedback = getPitchDirectionFeedback();
    const volumeFeedback = getVolumeDirectionFeedback();
    
    const needsSlowerSpeed = pitchFeedback.needsSlowerSpeed || 
                            (volumeFeedback && volumeFeedback.needsSlowerSpeed);
    
    let recommendedSpeed = "0.5x";
    if (pitchFeedback.needsSlowerSpeed && pitchFeedback.recommendedSpeed === "0.25x") {
      recommendedSpeed = "0.25x";
    }
    if (volumeFeedback && volumeFeedback.needsSlowerSpeed && volumeFeedback.recommendedSpeed === "0.25x") {
      recommendedSpeed = "0.25x";
    }

    if (correlation < 0) {
      return {
        priority: "High Priority",
        message: "Fix opposite pitch direction first.",
        action: "Focus on matching pitch direction.",
        color: "bg-red-50 border-red-200",
        needsSlowerSpeed: true,
        recommendedSpeed: "0.25x"
      };
    }
    
    if (hasIntensity && intensity_correlation < 0) {
      return {
        priority: "High Priority",
        message: "Fix opposite volume pattern.",
        action: "Match narrator's loudness pattern.",
        color: "bg-red-50 border-red-200",
        needsSlowerSpeed: true,
        recommendedSpeed: "0.25x"
      };
    }
    
    if (slope_diff < 0.08 && (!hasIntensity || intensity_slope_diff < 0.06)) {
      return {
        priority: "Excellent",
        message: "Perfect intonation patterns!",
        action: "Continue practicing to maintain.",
        color: "bg-green-50 border-green-200",
        needsSlowerSpeed: false
      };
    }
    
    if (slope_diff < 0.18 && (!hasIntensity || intensity_slope_diff < 0.15)) {
      return {
        priority: "Good Progress",
        message: "Good intonation patterns.",
        action: "Focus on subtle details.",
        color: "bg-yellow-50 border-yellow-200",
        needsSlowerSpeed: needsSlowerSpeed,
        recommendedSpeed: recommendedSpeed
      };
    }
    
    return {
      priority: "Needs Work",
      message: "Improve pitch and volume matching.",
      action: "Listen carefully to narrator trends.",
      color: "bg-orange-50 border-orange-200",
      needsSlowerSpeed: true,
      recommendedSpeed: "0.25x"
    };
  };

  const pitchFeedback = getPitchDirectionFeedback();
  const volumeFeedback = getVolumeDirectionFeedback();
  const overallFeedback = getOverallFeedback();

  const shouldShowSpeedSuggestions = overallFeedback.needsSlowerSpeed || 
                                    pitchFeedback.needsSlowerSpeed || 
                                    (volumeFeedback && volumeFeedback.needsSlowerSpeed);

  return (
    <div className="flex flex-col space-y-4 w-full max-w-full">
      <div className="text-center">
        <h2 className="text-xl font-bold text-gray-800">Intonation Direction Analysis</h2>
        <p className="text-sm text-gray-600 mt-1">
          Pitch and volume pattern matching
        </p>
      </div>

      {/* Speed Suggestion - Compact */}
      {shouldShowSpeedSuggestions && (
        <div className="p-3 rounded-lg border border-blue-300 bg-blue-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-xl mr-2">🐌</span>
              <div>
                <span className="font-medium text-blue-800">Try slower playback</span>
                <p className="text-xs text-blue-700">
                  Helps hear patterns more clearly
                </p>
              </div>
            </div>
            <button 
              onClick={() => setShowSpeedTips(!showSpeedTips)}
              className="px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded border border-blue-300 transition-colors"
            >
              {showSpeedTips ? "Hide" : "Tips"}
            </button>
          </div>
          
          {showSpeedTips && (
            <div className="mt-2 pt-2 border-t border-blue-200">
              <div className="text-xs space-y-2">
                <div className="flex items-center">
                  <span className="font-medium mr-2">Recommended:</span>
                  <span className="bg-blue-100 px-2 py-1 rounded">{overallFeedback.recommendedSpeed}</span>
                </div>
                <p className="text-blue-700">
                  Start slow, then increase speed as you improve.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Overall Summary - Compact */}
      <div className={`p-3 rounded-lg border ${overallFeedback.color}`}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold">Overall Assessment</h3>
          <span className="text-sm font-semibold">{overallFeedback.priority}</span>
        </div>
        <p className="text-sm mb-2">{overallFeedback.message}</p>
        <div className="text-xs bg-white p-2 rounded border">
          <span className="font-medium">💡 Action: </span>
          <span>{overallFeedback.action}</span>
        </div>
      </div>

      {/* Pitch Analysis - Compact */}
      <div className="border rounded-lg p-3">
        <div className="flex items-center mb-2">
          <span className="text-xl mr-2">🎵</span>
          <h3 className="font-bold">Pitch Direction</h3>
          <span className={`ml-auto px-2 py-1 rounded text-xs font-medium ${pitchFeedback.color.split(' ')[0]} ${pitchFeedback.color.split(' ')[1]}`}>
            {pitchFeedback.rating}
          </span>
        </div>
        
        <div className="space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-gray-50 p-1 rounded text-center">
              <div className="text-xs text-gray-600">Correlation</div>
              <div className={`font-bold ${correlation >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {correlation.toFixed(2)}
              </div>
            </div>
            <div className="bg-gray-50 p-1 rounded text-center">
              <div className="text-xs text-gray-600">Slope Diff</div>
              <div className="font-bold">{slope_diff.toFixed(3)}</div>
            </div>
          </div>
          
          <p className="text-sm">{pitchFeedback.message}</p>
          
          <div className="text-xs bg-blue-50 p-2 rounded border border-blue-100">
            <span className="font-medium">🎯 Tip: </span>
            <span>{pitchFeedback.suggestion}</span>
          </div>
        </div>
      </div>

      {/* Volume Analysis - Compact */}
      {hasIntensity && volumeFeedback && (
        <div className="border rounded-lg p-3">
          <div className="flex items-center mb-2">
            <span className="text-xl mr-2">📢</span>
            <h3 className="font-bold">Volume Pattern</h3>
            <span className={`ml-auto px-2 py-1 rounded text-xs font-medium ${volumeFeedback.color.split(' ')[0]} ${volumeFeedback.color.split(' ')[1]}`}>
              {volumeFeedback.rating}
            </span>
          </div>
          
          <div className="space-y-2 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-gray-50 p-1 rounded text-center">
                <div className="text-xs text-gray-600">Correlation</div>
                <div className={`font-bold ${intensity_correlation >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {intensity_correlation.toFixed(2)}
                </div>
              </div>
              <div className="bg-gray-50 p-1 rounded text-center">
                <div className="text-xs text-gray-600">Slope Diff</div>
                <div className="font-bold">{intensity_slope_diff.toFixed(3)}</div>
              </div>
            </div>
            
            <p className="text-sm">{volumeFeedback.message}</p>
            
            <div className="text-xs bg-blue-50 p-2 rounded border border-blue-100">
              <span className="font-medium">🔊 Tip: </span>
              <span>{volumeFeedback.suggestion}</span>
            </div>
          </div>
        </div>
      )}

      {/* Key Takeaways - Expandable */}
      <div className="border rounded-lg p-3 bg-gray-50">
        <button 
          onClick={() => setShowSpeedTips(!showSpeedTips)}
          className="w-full text-left flex items-center justify-between"
        >
          <div className="flex items-center">
            <span className="mr-2">📊</span>
            <span className="font-medium">Understanding Your Results</span>
          </div>
          <span>{showSpeedTips ? "▲" : "▼"}</span>
        </button>
        
        {showSpeedTips && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="space-y-1 text-xs">
              <div className="flex items-start">
                <span className="text-red-500 mr-1">•</span>
                <span><strong>Negative Correlation:</strong> Opposite pattern</span>
              </div>
              <div className="flex items-start">
                <span className="text-green-500 mr-1">•</span>
                <span><strong>Positive Correlation:</strong> Same direction</span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-500 mr-1">•</span>
                <span><strong>Small Slope Diff:</strong> Good degree match</span>
              </div>
              <div className="flex items-start">
                <span className="text-orange-500 mr-1">•</span>
                <span><strong>Large Slope Diff:</strong> Needs adjustment</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Practice Tips - Expandable */}
      <div className="border rounded-lg p-3 bg-purple-50 border-purple-100">
        <button 
          onClick={() => setShowMicTips(!showMicTips)}
          className="w-full text-left flex items-center justify-between"
        >
          <div className="flex items-center">
            <span className="mr-2">💪</span>
            <span className="font-medium">Practice Tips</span>
          </div>
          <span>{showMicTips ? "▲" : "▼"}</span>
        </button>
        
        {showMicTips && (
          <div className="mt-2 pt-2 border-t border-purple-200">
            <div className="space-y-1 text-xs">
              <div className="flex items-start">
                <span className="font-medium mr-1">1.</span>
                <span>Start with slower playback</span>
              </div>
              <div className="flex items-start">
                <span className="font-medium mr-1">2.</span>
                <span>Listen 2-3 times before recording</span>
              </div>
              <div className="flex items-start">
                <span className="font-medium mr-1">3.</span>
                <span>Pay attention to pitch direction</span>
              </div>
              <div className="flex items-start">
                <span className="font-medium mr-1">4.</span>
                <span>Notice volume emphasis</span>
              </div>
              <div className="flex items-start">
                <span className="font-medium mr-1">5.</span>
                <span>Gradually increase speed</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}